/**
 * INNTIL — pauseknapp og lastevakt for videoblokkene.
 *
 * Gjelder videoer der «Skjul avspillingskontroller» er huket av i blokka.
 * Blokka setter da klassen `inntil-videostyrt` paa <deferred-media>, og
 * Horizon lar vaere aa rendre nettleserens egne kontroller.
 *
 * HVORFOR EN KNAPP I DET HELE TATT. WCAG 2.2.2 «Pause, Stop, Hide» krever at
 * bevegelse som starter av seg selv og varer mer enn fem sekunder kan
 * pauses. Klippene i videogridet paa forsiden er 5,2-11,6 sekunder, og de
 * looper — en loop varer i praksis uendelig, saa femsekundersunntaket
 * gjelder ikke uansett hvor kort klippet er. Aa bare fjerne kontrollene
 * ville vaert et brudd paa nivaa A.
 *
 * HVORFOR EN LASTEVAKT. De fem klippene veier 62,8 MB til sammen, og
 * `autoplay` starter nedlastingen. Vi fjerner `autoplay`, setter
 * `preload="none"`, og lar en IntersectionObserver starte avspillingen
 * foerst naar videoen naermer seg skjermen. Samme grep som
 * `inntil-videoflis.js` i produktrutenettet — der er begrunnelsen maalt.
 *
 * BRUKERENS VALG VINNER. Har noen trykket pause, skal ikke observeren
 * starte videoen igjen naar den rulles ut og inn av skjermen. Derfor
 * `pausetAvBruker`.
 *
 * Bygget 25.09.2026.
 */
(() => {
  const MARGIN = '200px';
  const sett = new WeakSet();
  const iSyne = new Set();
  const pausetAvBruker = new WeakSet();

  /* Har brukeren bedt om mindre bevegelse, spiller vi ikke av noe av seg
     selv. Plakatbildet staar, og knappen tilbyr aa starte. Dette erstatter
     ikke knappen — 2.2.2 gjelder uansett — men det er riktig oppfoersel. */
  const mindreBevegelse = window.matchMedia('(prefers-reduced-motion: reduce)');

  const spill = (v) => {
    if (!v.paused || pausetAvBruker.has(v)) return;
    const lovnad = v.play();
    if (lovnad) lovnad.catch(() => {});
  };

  const merkKnapp = (knapp, spiller) => {
    knapp.setAttribute('aria-pressed', spiller ? 'false' : 'true');
    knapp.setAttribute('aria-label', spiller ? 'Pause video' : 'Spill av video');
    knapp.dataset.tilstand = spiller ? 'spiller' : 'pauset';
  };

  const lagKnapp = (vert, v) => {
    const knapp = document.createElement('button');
    knapp.type = 'button';
    knapp.className = 'inntil-videoknapp';
    /* To ikoner i markupen, ett synlig om gangen via CSS. Ingen flimring naar
       tilstanden skifter. SVG og ikke clip-path: en clip-path med prosenter
       forvrenges hvis boksen en dag ikke er kvadratisk, og det er nettopp
       det som skjedde her 25.09.2026. */
    knapp.innerHTML =
      '<svg class="inntil-videoknapp__ikon inntil-videoknapp__ikon--pause" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
      '<rect x="4" y="2" width="4" height="16" rx="1" fill="currentColor"/>' +
      '<rect x="12" y="2" width="4" height="16" rx="1" fill="currentColor"/></svg>' +
      '<svg class="inntil-videoknapp__ikon inntil-videoknapp__ikon--spill" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
      '<path d="M5 2.5 17 10 5 17.5Z" fill="currentColor"/></svg>';
    merkKnapp(knapp, !v.paused);

    knapp.addEventListener('click', () => {
      if (v.paused) {
        pausetAvBruker.delete(v);
        const lovnad = v.play();
        if (lovnad) lovnad.catch(() => {});
      } else {
        pausetAvBruker.add(v);
        v.pause();
      }
    });

    v.addEventListener('play', () => merkKnapp(knapp, true));
    v.addEventListener('pause', () => merkKnapp(knapp, false));

    /* FOERST, IKKE SIST. Horizon har regelen
         deferred-media > .deferred-media__poster-button ~ *:not(template)
         { position:absolute; top:0; left:0; width:100%; height:100% }
       som strekker ALT som staar etter plakatknappen til full stoerrelse.
       La vi knappen sist, ble den en skjerm-fyllende ellipse over videoen.
       Funnet av Benedikte 25.09.2026. Som foerste barn treffer ikke
       soeskenselektoren den i det hele tatt. CSS-en har i tillegg en
       defensiv regel, i tilfelle rekkefoelgen endrer seg. */
    vert.prepend(knapp);
  };

  const vakt = new IntersectionObserver(
    (treff) => {
      for (const t of treff) {
        const v = t.target;
        if (t.isIntersecting) {
          iSyne.add(v);
          if (!mindreBevegelse.matches) spill(v);
        } else {
          iSyne.delete(v);
          if (!v.paused) v.pause();
        }
      }
    },
    { rootMargin: MARGIN, threshold: 0.1 }
  );

  const koble = (rot) => {
    const verter = (rot instanceof Element ? rot : document).querySelectorAll('.inntil-videostyrt');
    for (const vert of verter) {
      const v = vert.querySelector('video');
      if (!v || sett.has(v)) continue;
      sett.add(v);

      /* Fjern autoplay FOER observeren kobles, ellers har nedlastingen
         allerede startet. `muted` maa settes som egenskap, ikke bare
         attributt: Safari nekter aa spille uten brukerhandling ellers. */
      v.removeAttribute('autoplay');
      v.autoplay = false;
      v.preload = 'none';
      v.muted = true;
      if (!v.paused) v.pause();

      lagKnapp(vert, v);
      vakt.observe(v);
    }
  };

  const start = () => {
    koble(document);
    /* Seksjoner rendres om i temaeditoren og ved navigasjon. Nye videoer
       kommer da inn uten at noe fyrer. Samme form som videoflisene. */
    new MutationObserver((endringer) => {
      for (const e of endringer) {
        for (const n of e.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches('.inntil-videostyrt')) koble(n.parentElement || document);
          else if (n.querySelector && n.querySelector('.inntil-videostyrt')) koble(n);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  /* Chrome pauser video uten lydspor naar siden er skjult, for aa spare
     stroem, og observeren fyrer ikke naar fanen kommer tilbake fordi
     videoen ikke har flyttet seg. Maalt 25.09.2026 paa produktrutenettet. */
  const gjenoppta = () => {
    if (document.visibilityState !== 'visible' || mindreBevegelse.matches) return;
    for (const v of iSyne) spill(v);
  };
  document.addEventListener('visibilitychange', gjenoppta);
  window.addEventListener('pageshow', gjenoppta);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
